import axios from "axios";

// Add base URL configuration
// If REACT_APP_API_URL is set to empty string, use relative URLs (same domain)
// Otherwise use the provided URL or default to localhost for development
const API_URL = process.env.REACT_APP_API_URL !== undefined 
  ? process.env.REACT_APP_API_URL 
  : "http://localhost:8000";

export const createPost = async (
  type,
  background,
  text,
  images,
  user,
  token,
  feeling,
  activity,
  location,
  taggedFriends
) => {
  try {
    // Prepare the payload, only include feeling/activity if they have valid values
    const payload = {
      type,
      background,
      text,
      images,
      user,
    };
    // Only add feeling/activity if they are not null, undefined, or empty string
    if (feeling && feeling !== null && feeling !== undefined && feeling.trim() !== "") {
      payload.feeling = feeling;
    }
    if (activity && activity !== null && activity !== undefined && activity.trim() !== "") {
      payload.activity = activity;
    }
    // Add location if provided and has meaningful data
    if (location && location !== null && location !== undefined && typeof location === 'object') {
      // Check if location has any meaningful data
      const hasLocationData = location.name || 
                             location.address || 
                             (location.lat !== undefined && location.lat !== null) ||
                             (location.lng !== undefined && location.lng !== null) ||
                             location.type;
      if (hasLocationData) {
        payload.location = location;
      }
    }
    // Add tagged friends if provided
    if (taggedFriends && taggedFriends.length > 0) {
      payload.taggedFriends = taggedFriends.map(friend => friend._id);
    }
    // console.log("createPost payload:", payload);
    // console.log("Location data being sent:", location);
    const { data } = await axios.post(
      `${API_URL}/createPost`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok", data };
  } catch (error) {
    console.log("createPost error:", error);
    console.log("Error response:", error.response);
    console.log("Error response data:", JSON.stringify(error.response?.data));
    
    let errorMessage = "Failed to create publication";
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
      // If it's a validation error, add the details
      if (error.response.data.message === "Validation error" && error.response.data.details) {
        errorMessage += ": " + (Array.isArray(error.response.data.details) 
          ? error.response.data.details.join(", ") 
          : error.response.data.details);
      }
    } else if (error.response?.data?.details) {
      errorMessage = Array.isArray(error.response.data.details) 
        ? error.response.data.details.join(", ") 
        : error.response.data.details;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    console.log("Final error message:", errorMessage);
    return { status: "error", message: errorMessage };
  }
};

// Create a group post
export const createGroupPost = async (
  type,
  background,
  text,
  images,
  user,
  groupId,
  token,
  feeling,
  activity,
  location
) => {
  try {
    // Prepare the payload, only include feeling/activity if they have valid values
    const payload = {
      type,
      background,
      text,
      images,
      user,
      groupId,
    };
    // Only add feeling/activity if they are not null, undefined, or empty string
    if (feeling && feeling !== null && feeling !== undefined && feeling.trim() !== "") {
      payload.feeling = feeling;
    }
    if (activity && activity !== null && activity !== undefined && activity.trim() !== "") {
      payload.activity = activity;
    }
    // Add location if provided and has meaningful data
    if (location && location !== null && location !== undefined && typeof location === 'object') {
      // Check if location has any meaningful data
      const hasLocationData = location.name || 
                             location.address || 
                             (location.lat !== undefined && location.lat !== null) ||
                             (location.lng !== undefined && location.lng !== null) ||
                             location.type;
      if (hasLocationData) {
        payload.location = location;
      }
    }
    console.log("createGroupPost payload:", payload);
    const { data } = await axios.post(
      `${API_URL}/createGroupPost`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok", data };
  } catch (error) {
    console.log("createGroupPost error:", error);
    console.log("Error response data:", JSON.stringify(error.response?.data));
    
    let errorMessage = "Failed to create group post";
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
      // If it's a validation error, add the details
      if (error.response.data.message === "Validation error" && error.response.data.details) {
        errorMessage += ": " + (Array.isArray(error.response.data.details) 
          ? error.response.data.details.join(", ") 
          : error.response.data.details);
      }
    } else if (error.response?.data?.details) {
      errorMessage = Array.isArray(error.response.data.details) 
        ? error.response.data.details.join(", ") 
        : error.response.data.details;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return { status: "error", message: errorMessage };
  }
};

// Get posts for a specific group
export const getGroupPosts = async (groupId, token) => {
  try {
    const { data } = await axios.get(
      `${API_URL}/getGroupPosts/${groupId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok", data };
  } catch (error) {
    return { status: "error", message: error.response.data.message };
  }
};

export const reactPost = async (postId, react, token) => {
  try {
  await axios.put(
      `${API_URL}/reactPost`,
      {
        postId,
        react,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return "ok";
  } catch (error) {
    return error.response.data.message;
  }
};

export const getReacts = async (postId, token) => {
  try {
    const { data } = await axios.get(
      `${API_URL}/getReacts/${postId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response.data.message;
  }
};

export const comment = async (postId, comment, image, token, gifUrl = "", stickerUrl = "") => {
  try {
    console.log("Comment function called with:", { postId, comment, image, gifUrl, stickerUrl });
    
    const { data } = await axios.put(
      `${API_URL}/comment`,
      {
        postId,
        comment,
        image,
        gifUrl,
        stickerUrl,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    console.log("Comment API response:", data);
    return data;
  } catch (error) {
    console.error("Comment API error:", error.response?.data || error.message);
    return error.response?.data?.message || "Failed to post comment";
  }
};

export const savePost = async (postId, token) => {
  try {
    const { data } = await axios.put(
      `${API_URL}/savePost/${postId}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response.data.message;
  }
};

export const deletePost = async (postId, token) => {
  try {
    const apiUrl = `${API_URL}/deletePost/${postId}`;
    const response = await fetch(apiUrl, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await response.json();
    return data;
  } catch (error) {
    return error;
  }
};

export const replyToComment = async (postId, parentCommentId, reply, image, token) => {
  try {
    const { data } = await axios.put(
      `${API_URL}/replyToComment`,
      {
        postId,
        parentCommentId,
        reply,
        image,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response.data.message;
  }
};

// Delete comment image
export const deleteCommentImage = async (postId, commentId, token) => {
  try {
    const { data } = await axios.delete(
      `${API_URL}/deleteCommentImage/${postId}/${commentId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response?.data?.message || "Failed to delete image";
  }
};

// Delete reply image
export const deleteReplyImage = async (postId, commentId, replyId, token) => {
  try {
    const { data } = await axios.delete(
      `${API_URL}/deleteReplyImage/${postId}/${commentId}/${replyId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response?.data?.message || "Failed to delete reply image";
  }
};

// Delete a reply (text/image) permanently
export const deleteReply = async (postId, commentId, replyId, token) => {
  try {
    const { data } = await axios.delete(
      `${API_URL}/deleteReply/${postId}/${commentId}/${replyId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response?.data?.message || "Failed to delete reply";
  }
};

// Delete a top-level comment
export const deleteComment = async (postId, commentId, token) => {
  try {
    const { data } = await axios.delete(
      `${API_URL}/deleteComment/${postId}/${commentId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response?.data?.message || "Failed to delete comment";
  }
};

// Edit a reply
export const editReply = async (postId, commentId, replyId, reply, token) => {
  try {
    const { data } = await axios.put(
      `${API_URL}/editReply`,
      { postId, commentId, replyId, reply },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
  } catch (error) {
    return error.response?.data?.message || 'Failed to edit reply';
  }
};
